// src/pages/Costs.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { FaMoneyBill } from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Costs() {
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!localStorage.getItem("auth")) window.location.href = "/login";
  }, []);

  useEffect(() => {
    async function fetchCosts() {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/costs`);
        setCosts(res.data || []);
      } catch (err) {
        console.error("Error fetching costs:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchCosts();
  }, []);

  const totalCost = costs.reduce((s, r) => s + (r.total_cost || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center gap-3 text-slate-400">
          <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm font-medium">Loading costs…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-screen-xl space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Cost Calculation</h1>
          <p className="text-sm text-slate-500 mt-0.5">Daily breakdown of labor and equipment costs</p>
        </div>
        {costs.length > 0 && (
          <div className="px-4 py-2.5 rounded-2xl bg-blue-50 border border-blue-100 text-right">
            <div className="text-xs text-blue-500 font-medium">Total Accumulated</div>
            <div className="text-xl font-bold text-blue-700">${totalCost.toLocaleString()}</div>
          </div>
        )}
      </div>

      {costs.length === 0 ? (
        <div className="card flex flex-col items-center justify-center py-20 gap-3">
          <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
            <FaMoneyBill className="text-slate-300 text-lg" />
          </div>
          <div className="text-sm text-slate-500">No cost data found.</div>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <div className="section-title">Cost Records</div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  {["Date", "Persons", "Machinery", "Total Cost"].map((h) => (
                    <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                <AnimatePresence>
                  {costs.map((row, idx) => (
                    <motion.tr
                      key={row.date}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2, delay: idx * 0.04 }}
                      className="hover:bg-slate-50 transition-colors duration-100"
                    >
                      <td className="px-5 py-3 font-medium text-slate-800">{row.date}</td>
                      <td className="px-5 py-3 text-slate-600">{row.person_total}</td>
                      <td className="px-5 py-3 text-slate-600">{row.machinery_total}</td>
                      <td className="px-5 py-3">
                        <span className="font-semibold text-blue-700">
                          ${row.total_cost.toLocaleString()}
                        </span>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-200 bg-slate-50">
                  <td className="px-5 py-3 font-semibold text-slate-600" colSpan={3}>Grand Total</td>
                  <td className="px-5 py-3 font-bold text-blue-800">
                    ${totalCost.toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default Costs;
